// The one-off "keep this window open" notice, and the bridge that lets the download wait for
// it. See features/modals/BrowserDownloaderNoticeModal.tsx for what it says and why.
import { modal } from "../../ui/modal";
import { api } from "../../api";

/**
 * Resolver for the modal currently on screen, or null when none is.
 *
 * A module-level slot rather than a window global — there is no window bridge in this bundle
 * and adding one would put a function somewhere `check:inline-js` cannot see. The modal
 * system is fire-and-forget (`modal(key, size)` returns void), so something has to carry the
 * "it closed" signal back, and one slot is enough: only one download can be starting at a
 * time, because `download()` awaits this before doing anything else.
 */
let pending: (() => void) | null = null;

/** Called by the modal when it unmounts, however it was closed. */
export function resolveBrowserDownloaderNotice(): void {
  const done = pending;
  pending = null;
  done?.();
}

/**
 * Show the notice and resolve once the reseller has dismissed it. Resolves immediately when
 * they have seen it before.
 *
 * The acknowledgement is written as the modal opens, not when it closes, and deliberately:
 * the point is that they were shown it. Writing on close would mean a reseller who closed
 * the tab mid-notice gets it again, and — worse — the write would race the download that
 * starts in the same tick.
 *
 * A failed write is swallowed. The cost is seeing the notice twice; throwing here would
 * block a download over a bookkeeping row.
 */
export function showBrowserDownloaderNotice(alreadySeen: boolean): Promise<void> {
  if (alreadySeen) return Promise.resolve();

  void api.downloadCart.ackBrowserDownloaderNotice().catch(() => undefined);

  return new Promise<void>((resolve) => {
    // If one is somehow already open, let the older wait finish rather than strand it.
    resolveBrowserDownloaderNotice();
    pending = resolve;
    // "dynamic": the notice is short and a fixed size leaves most of the panel empty.
    modal("downloadernotice", "dynamic");
  });
}
